import { Input, Space, Table } from 'antd';
import React, { useState, useRef } from 'react';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import localeObjectTable from '../../Utils/localeObjectTable';
import PropTypes from 'prop-types';
import currencyFormatter from '../../Utils/currencyFormatter';
import { DatePicker } from 'antd';
import moment from 'moment';
import { photosEndpoint } from '../../Constants/apiEndpoint';
import buildPhotoUrl from '../../Utils/buildPhotoUrl';
import es from 'antd/lib/date-picker/locale/es_ES';
import Boton from '../Atoms/Boton';

const TablaEgresoEfectivo = ({ data, loading }) => {
  const { RangePicker } = DatePicker;
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: '0 8px 8px 8px'
        }}
        onKeyDown={(e) => e.stopPropagation()}>
        <Boton
          type="link"
          size="small"
          onClick={() => {
            close();
          }}
          className="button-close-search-filter-table">
          X
        </Boton>
        <Input
          ref={searchInput}
          placeholder={`Buscar`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block'
          }}
        />
        <Space>
          <Boton
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small">
            Buscar
          </Boton>
          <Boton onClick={() => clearFilters && handleReset(clearFilters)} size="small" danger>
            Limpiar
          </Boton>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1890ff' : undefined
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: '#ffc069',
            padding: 0
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      )
  });

  const openFile = (file) => {
    if (file) {
      const url = buildPhotoUrl(photosEndpoint, file);
      window.open(url);
    }
  };

  const tablaReporte = [
    {
      title: 'PERIODO',
      dataIndex: 'period',
      key: 'period',
      sortDirections: ['ascend', 'descend'],
      sorter: (a, b) => a.period.localeCompare(b.period),
      filters: [
        { text: 'Enero', value: 'January' },
        { text: 'Febrero', value: 'February' },
        { text: 'Marzo', value: 'March' },
        { text: 'Abril', value: 'April' },
        { text: 'Mayo', value: 'May' },
        { text: 'Junio', value: 'June' },
        { text: 'Julio', value: 'July' },
        { text: 'Agosto', value: 'August' },
        { text: 'Septiembre', value: 'September' },
        { text: 'Octubre', value: 'October' },
        { text: 'Noviembre', value: 'November' },
        { text: 'Diciembre', value: 'December' }
      ],
      onFilter: (value, record) => record.period === value,
      render: (text) => {
        const monthsInSpanish = {
          January: 'Enero',
          February: 'Febrero',
          March: 'Marzo',
          April: 'Abril',
          May: 'Mayo',
          June: 'Junio',
          July: 'Julio',
          August: 'Agosto',
          September: 'Septiembre',
          October: 'Octubre',
          November: 'Noviembre',
          December: 'Diciembre'
        };
        const translatedText = monthsInSpanish[text] || text;
        return translatedText;
      }
    },
    {
      title: 'FECHA DE DEVOLUCIÓN',
      dataIndex: 'created_at',
      key: 'created_at',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
        <div
          style={{
            padding: '0 8px 8px 8px'
          }}
          onKeyDown={(e) => e.stopPropagation()}>
          <Boton
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
            className="button-close-search-filter-table">
            X
          </Boton>
          <RangePicker
            locale={es}
            placeholder={['Fecha inicial', 'Fecha final']}
            value={selectedKeys[0]}
            onChange={(dates) => {
              setSelectedKeys([dates]);
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Boton
              type="primary"
              onClick={() => {
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small">
              Buscar
            </Boton>
            <Boton onClick={clearFilters} size="small" danger>
              Limpiar
            </Boton>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{
            color: filtered ? '#1890ff' : undefined
          }}
        />
      ),
      onFilter: (value, record) => {
        if (!value) return true;
        const start = value[0].startOf('day').toDate(); // Convertir a objeto Date
        const end = value[1].endOf('day').toDate(); // Convertir a objeto Date
        const created = moment(record.created_at, 'DD-MM-YYYY').toDate(); // Convertir a objeto Date
        return created >= start && created <= end;
      },
      sorter: (a, b) => moment(a.created_at, 'DD-MM-YYYY') - moment(b.created_at, 'DD-MM-YYYY'),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'TOTAL REGRESADO',
      dataIndex: 'total_returned',
      key: 'total_returned',
      ...getColumnSearchProps('total_returned'),
      sorter: (a, b) => a.total_returned - b.total_returned,
      sortDirections: ['ascend', 'descend'],
      render: (text) => currencyFormatter(text)
    },
    {
      title: 'REGRESÓ A',
      dataIndex: 'was_returned_to',
      key: 'was_returned_to',
      ...getColumnSearchProps('was_returned_to'),
      sorter: (a, b) => a.was_returned_to.localeCompare(b.was_returned_to),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'DEVUELTO POR',
      dataIndex: 'id_user',
      key: 'id_user',
      ...getColumnSearchProps('id_user'),
      sorter: (a, b) => a.id_user.localeCompare(b.id_user),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'ARCHIVO',
      dataIndex: 'acciones',
      key: 'acciones'
    }
  ];

  const newDataSOL = data?.map((item, index) => {
    return {
      key: index,
      total_returned: item?.total_returned,
      total_spent: item?.total_spent,
      period: item?.period,
      was_returned_to: item?.was_returned_to,
      id_user: item?.id_user,
      file: item?.file,
      created_at: item?.created_at,
      acciones: (
        <Boton type="link" size="small" onClick={() => openFile(item?.file)}>
          <EyeOutlined />
          Ver Archivo
        </Boton>
      )
    };
  });
  return (
    <>
      <Table
        loading={loading}
        columns={tablaReporte}
        dataSource={newDataSOL}
        pagination={{
          position: ['bottomCenter'],
          defaultPageSize: 10,
          pageSizeOptions: [20, 40, 60, 80, 100],
          total: data.total,
          showTotal: (total, range) =>
            `Mostrando egresos del ${range[0]} al ${range[1]} de un total de ${total}`
        }}
        className="mt-4 mb-4"
        locale={localeObjectTable}
        scroll={{
          x: true
        }}
      />
    </>
  );
};

TablaEgresoEfectivo.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool
};
export default TablaEgresoEfectivo;
